import { Drawer, message, Modal, Tooltip } from 'antd';
import BigNumber from 'bignumber.js';
import { ArrowDownToLine, ArrowUpFromLine, Check, Copy, LogOut, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ellipseAddress } from 'src/helpers';
import { removeAuthTokens } from 'src/helpers/storage';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppTranslation } from 'src/i18n/hooks';
import { useAuth } from 'src/libs/rainbow';
import { queryClient } from 'src/libs/react-query';
import { userKeys } from 'src/services/user/user-keys';
import { emojiAvatarForAddress } from 'src/utils/emojiAvatarForAddress';
import { Address } from 'viem';
import { useBalance, useDisconnect } from 'wagmi';
import { useClipboard } from 'use-clipboard-copy';

import './styles.scss';
import { useGetAllTimeLeaderboard } from 'src/services/leaderboard/get-all-time-leaderboard';
import { getTierImage } from 'src/pages/trading-leaderboard';
import { useAppDispatch } from 'src/store';
import { clearUserInfo } from 'src/store/actions/user';
import { useWalletSelector } from '@near-wallet-selector/react-hook';
import { useGetUserInfo } from 'src/services/user/get-user-info';

interface Props {
  open: boolean;
  onClose: () => void;
  walletAddress?: string;
  onDeposit?: () => void;
  onWithdraw?: () => void;
}

const WalletInformationModal = ({ open, onClose, walletAddress, onDeposit, onWithdraw }: Props) => {
  const userTheme = useSelector((state: any) => state.theme);
  const [copyApiBlocked, setCopyApiBlocked] = useState(false);
  const { isMobile, isTablet } = useWindowSize();
  const { disconnectAsync } = useDisconnect();
  const { signOut } = useWalletSelector();
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation(['common']);

  const { data: userData } = useGetUserInfo(localStorage.getItem('userId') ?? '');
  const isTestAccount = userData?.role === 'PAPER_ACCOUNT';

  const { setAuthStatus, isEthereumWallet } = useAuth();
  const { copy, copied } = useClipboard({
    copiedTimeout: 1000,
    onError: () => {
      message.error(t('wallet.copyApiBlockedWarning'));
      setCopyApiBlocked(true);
    },
  });

  const { data: AllTimeLeaderboardData } = useGetAllTimeLeaderboard(
    {
      page: 1,
      page_size: 10,
      useraddress: walletAddress,
    },
    {
      enabled: !!walletAddress,
    },
  );

  const { emoji, color } = useMemo(
    () => emojiAvatarForAddress(walletAddress ?? ''),
    [walletAddress],
  );

  const { data } = useBalance({
    address: walletAddress as Address,
  });
  const handleCopyAddress = async () => {
    copy(walletAddress);
  };

  const handleDisconnect = async () => {
    removeAuthTokens();
    setAuthStatus('unauthenticated');
    dispatch(clearUserInfo());
    onClose?.();
    try {
      if (isEthereumWallet) {
        await disconnectAsync();
      } else {
        await signOut();
      }
    } catch {}
    onClose?.();
    await queryClient.resetQueries({
      queryKey: userKeys.all,
    });
  };

  const renderTierImage = (isDrawer?: boolean) => {
    if (isTestAccount) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          className="lucide lucide-user-lock-icon lucide-user-lock"
        >
          <path d="M19 16v-2a2 2 0 0 0-4 0v2"></path>
          <path d="M9.5 15H7a4 4 0 0 0-4 4v2"></path>
          <circle cx="10" cy="7" r="4"></circle>
          <rect x="13" y="16" width="8" height="5" rx=".899"></rect>
        </svg>
      );
    }
    return getTierImage(AllTimeLeaderboardData?.user?.tier) ? (
      <img
        className={`${isDrawer ? 'w-[30px] h-[30px]' : 'w-[60px] h-[60px]'}`}
        src={getTierImage(AllTimeLeaderboardData?.user?.tier)}
      />
    ) : (
      emoji
    );
  };

  return (
    <>
      {isMobile || isTablet ? (
        <Drawer
          id="wallet-information"
          className={userTheme}
          placement={'bottom'}
          closable={false}
          onClose={onClose}
          open={open}
          height={320}
        >
          <div className="relative">
            <div
              onClick={onClose}
              className="absolute right-[0] top-[0] rounded-[100px] bg-[#0000000f] p-[5px] text-[#3c4242cc]"
            >
              <XIcon size={20} />
            </div>
          </div>
          <div className="flex justify-center item-center">
            <div
              style={{
                backgroundColor: color,
              }}
              className="flex justify-center items-center w-[82px] h-[82px] rounded-[100px] text-[45px] mt-[30px]"
            >
              {renderTierImage()}
            </div>
          </div>
          <p className="text-[20px] font-[700] text-center mt-[20px] dark:text-white text-wrap">
            <span>{copyApiBlocked ? walletAddress : ellipseAddress(walletAddress ?? '')}</span>
            {copied ? (
              <Check className="dark:text-white inline-block ml-[4px]" size={16} />
            ) : (
              <Copy
                onClick={handleCopyAddress}
                className="dark:text-white inline-block ml-[4px]"
                size={16}
              />
            )}
          </p>
          <p className=" text-center text-[16px] font-[600] text-[#3c424299] dark:text-[#fff9]">
            {BigNumber(data?.formatted ?? '0').toFixed(2)} {data?.symbol}
          </p>
          <div className="flex flex-col gap-[10px] mt-[10px]">
            <div className="flex gap-[10px]">
              <div
                onClick={() => onDeposit?.()}
                className="flex flex-1 flex-row items-center justify-center gap-2 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[8px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a] cursor-pointer"
              >
                <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                  {t('wallet.deposit')}
                </span>
                <ArrowDownToLine className="dark:text-white" size={18} />
              </div>
              {isTestAccount ? (
                <Tooltip title={t('account.testAccount')}>
                  <button
                    onClick={() => onWithdraw?.()}
                    className="flex flex-1 flex-row items-center justify-center gap-2 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[8px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a] cursor-pointer"
                  >
                    <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                      {t('wallet.withdraw')}
                    </span>
                    <ArrowUpFromLine className="dark:text-white" size={18} />
                  </button>
                </Tooltip>
              ) : (
                <button
                  onClick={() => onWithdraw?.()}
                  className="flex flex-1 flex-row items-center justify-center gap-2 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[8px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a] cursor-pointer"
                >
                  <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                    {t('wallet.withdraw')}
                  </span>
                  <ArrowUpFromLine className="dark:text-white" size={18} />
                </button>
              )}
            </div>
            <div
              onClick={handleDisconnect}
              className="flex flex-row w-full items-center justify-center gap-2 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[8px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a] cursor-pointer"
            >
              <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                {t('wallet.disconnect')}
              </span>
              <LogOut size={18} />
            </div>
          </div>
        </Drawer>
      ) : (
        <Modal
          className={`wallet-information-modal ${userTheme}`}
          centered
          open={open}
          onClose={onClose}
          onCancel={onClose}
          closable={false}
          footer={false}
          width={360}
          height={240}
        >
          <div className="relative">
            <div
              onClick={onClose}
              className="absolute right-[0] top-[0] rounded-[100px] bg-[#0000000f] dark:bg-[#ffffff14] p-[5px] text-[#3c4242cc] cursor-pointer"
            >
              <XIcon width={14} height={14} className="dark:text-[#e0e8ff99]" size={20} />
            </div>
          </div>
          <div className="flex justify-center item-center">
            <div
              style={{
                backgroundColor: !getTierImage(AllTimeLeaderboardData?.user?.tier)
                  ? color
                  : undefined,
              }}
              className="flex justify-center items-center w-[74px] h-[74px] rounded-[100px] text-[45px] mt-[6px]"
            >
              {renderTierImage()}
            </div>
          </div>
          <p className="text-[16px] font-[700] text-center mt-[20px] lg:mt-[10px] dark:text-white">
            {copyApiBlocked ? walletAddress : ellipseAddress(walletAddress ?? '')}
            {copied ? (
              <Check className="dark:text-white inline-block ml-[4px]" size={16} />
            ) : (
              <Copy
                onClick={handleCopyAddress}
                className="dark:text-white inline-block ml-[4px]"
                size={16}
              />
            )}
          </p>
          <p className=" text-center text-[16px] font-[600] text-[#3c424299] dark:text-[#fff9]">
            {BigNumber(data?.formatted ?? '0').toFixed(2)} {data?.symbol}
          </p>
          <div className="flex flex-col gap-[10px] mt-[10px]">
            <div className="flex gap-[10px]">
              <div
                onClick={() => onDeposit?.()}
                className="flex flex-1 flex-row items-center justify-center gap-2 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[8px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a] cursor-pointer"
              >
                <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                  {t('wallet.deposit')}
                </span>
                <ArrowDownToLine className="dark:text-white" size={18} />
              </div>
              {isTestAccount ? (
                <Tooltip title={t('account.testAccount')}>
                  <button
                    onClick={() => onWithdraw?.()}
                    className="flex flex-1 flex-row items-center justify-center gap-2 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[8px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a] cursor-pointer"
                  >
                    <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                      {t('wallet.withdraw')}
                    </span>
                    <ArrowUpFromLine className="dark:text-white" size={18} />
                  </button>
                </Tooltip>
              ) : (
                <button
                  onClick={() => onWithdraw?.()}
                  className="flex flex-1 flex-row items-center justify-center gap-2 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[8px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a] cursor-pointer"
                >
                  <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                    {t('wallet.withdraw')}
                  </span>
                  <ArrowUpFromLine className="dark:text-white" size={18} />
                </button>
              )}
            </div>
            <div
              onClick={handleDisconnect}
              className="flex flex-row w-full items-center justify-center gap-2 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[8px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a] cursor-pointer"
            >
              <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
                {t('wallet.disconnect')}
              </span>
              <LogOut size={18} />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default WalletInformationModal;
